import {
  objectParser,
  identifierAt,
  text,
  MAX_MESSAGE_TEXT,
  valueTextAt,
  confidenceAt,
  oneOf,
  nullable,
  nonBlankTextAt,
  booleanAt,
  exactObject,
  integerAt,
  fail,
  boundedIntegerAt,
  MAX_COLLECTION_SIZE,
  arrayOf,
  nullableAt,
  enumAt,
  hasOwn,
  arrayAt,
  assertUnique,
  objectAt
} from './primitives';
import {
  type PageReviewRow,
  PAGE_FIELD_STATES,
  PAGE_FIELD_WRITE_STATES,
  SMART_FILL_DECISION_STATUSES,
  type LearnedRule,
  LEARNED_RULE_ACTIONS,
  type PageSessionCounts,
  type ContinuousReviewDebt,
  type ContinuousFillState,
  CONTINUOUS_FILL_STATUSES,
  CONTINUOUS_WAITING_REASONS,
  type FillOutcome,
  FILL_OUTCOME_KINDS,
  type FillSelection,
  type PageSessionSnapshot,
  PAGE_SESSION_PHASES,
  FILL_MODES
} from '../dtos';
import {
  type RuleClearPayload,
  type FillStartOncePayload,
  type FillStartContinuousPayload,
  type FocusReviewFieldPayload,
  type SaveReviewEditPayload
} from '../protocol';
import { parseFillSource } from './buckets';
import { parseBridgeError, parseBridgeWarning } from './context';

export const parsePageReviewRow = objectParser<PageReviewRow>(
  {
    fieldId: identifierAt,
    label: text(MAX_MESSAGE_TEXT),
    value: valueTextAt,
    confidence: confidenceAt,
    state: oneOf(PAGE_FIELD_STATES),
    writeState: oneOf(PAGE_FIELD_WRITE_STATES),
    decisionStatus: oneOf(SMART_FILL_DECISION_STATUSES),
    reason: nullable(nonBlankTextAt),
    propertyId: nullable(identifierAt),
    focused: booleanAt
  },
  { optional: { writeReason: identifierAt } }
);

export const parseLearnedRule = objectParser<LearnedRule>({
  id: identifierAt,
  fieldLabel: nullable(text(MAX_MESSAGE_TEXT)),
  action: oneOf(LEARNED_RULE_ACTIONS),
  meaning: nullable(text(MAX_MESSAGE_TEXT)),
  updatedAt: nonBlankTextAt
});

export const ruleClearPayloadAt = objectParser<RuleClearPayload>({ ruleId: identifierAt });

export function parsePageSessionCounts(value: unknown, path = '$'): PageSessionCounts {
  const object = exactObject(value, path, ['total', 'filled', 'review', 'empty']);
  const total = integerAt(object.total, `${path}.total`);
  const filled = integerAt(object.filled, `${path}.filled`);
  const review = integerAt(object.review, `${path}.review`);
  const empty = integerAt(object.empty, `${path}.empty`);
  if (filled > total || review > total || empty > total || review + empty > total) {
    fail(path, 'field counts are inconsistent with total');
  }
  return value as PageSessionCounts;
}

const continuousReviewDebtAt = objectParser<ContinuousReviewDebt>({
  step: boundedIntegerAt(MAX_COLLECTION_SIZE),
  reviewCount: integerAt
});

export const parseContinuousFillState = objectParser<ContinuousFillState>({
  sessionId: identifierAt,
  status: oneOf(CONTINUOUS_FILL_STATUSES),
  stepCount: integerAt,
  cumulativeFilledCount: integerAt,
  cumulativeReviewCount: integerAt,
  waitingReason: nullable(oneOf(CONTINUOUS_WAITING_REASONS)),
  reviewDebt: arrayOf(continuousReviewDebtAt)
});

const parseFillOutcome = objectParser<FillOutcome>({
  kind: oneOf(FILL_OUTCOME_KINDS),
  summary: text(MAX_MESSAGE_TEXT)
});

const parseFillSelection = objectParser<FillSelection>({
  bucketId: identifierAt,
  scopeEntityId: nullable(identifierAt),
  revision: integerAt
});

export function parsePageSessionSnapshot(value: unknown, path = '$'): PageSessionSnapshot {
  const object = exactObject(
    value,
    path,
    [
      'sessionId',
      'phase',
      'mode',
      'source',
      'counts',
      'reviewRows',
      'continuous',
      'outcome',
      'error',
      'warning'
    ],
    ['selection', 'formRevision']
  );
  nullableAt(object.sessionId, `${path}.sessionId`, identifierAt);
  if (hasOwn(object, 'formRevision'))
    nonBlankTextAt(object.formRevision, `${path}.formRevision`, 8_192);
  enumAt(object.phase, PAGE_SESSION_PHASES, `${path}.phase`);
  enumAt(object.mode, FILL_MODES, `${path}.mode`);
  nullableAt(object.source, `${path}.source`, parseFillSource);
  if (hasOwn(object, 'selection')) parseFillSelection(object.selection, `${path}.selection`);
  parsePageSessionCounts(object.counts, `${path}.counts`);
  const rows = arrayAt(object.reviewRows, `${path}.reviewRows`, parsePageReviewRow);
  assertUnique(rows, (row) => row.fieldId, `${path}.reviewRows`, 'fieldId');
  if (rows.filter((row) => row.focused).length > 1) {
    fail(`${path}.reviewRows`, 'at most one review row may be focused');
  }
  nullableAt(object.continuous, `${path}.continuous`, parseContinuousFillState);
  nullableAt(object.outcome, `${path}.outcome`, parseFillOutcome);
  nullableAt(object.error, `${path}.error`, parseBridgeError);
  nullableAt(object.warning, `${path}.warning`, parseBridgeWarning);
  return value as PageSessionSnapshot;
}

export const fillStartOncePayloadAt = objectParser<FillStartOncePayload>(
  {},
  { optional: { scopeEntityId: nullable(identifierAt) } }
);

export const fillStartContinuousPayloadAt = objectParser<FillStartContinuousPayload>(
  {},
  { optional: { scopeEntityId: nullable(identifierAt) } }
);

export const focusReviewFieldPayloadAt = objectParser<FocusReviewFieldPayload>({
  fieldId: identifierAt
});

export function saveReviewEditPayloadAt(value: unknown, path: string): SaveReviewEditPayload {
  const object = exactObject(value, path, ['fieldId', 'value'], ['teach']);
  identifierAt(object.fieldId, `${path}.fieldId`);
  valueTextAt(object.value, `${path}.value`);
  if (hasOwn(object, 'teach')) {
    const teach = objectAt(object.teach, `${path}.teach`);
    const action = enumAt(teach.action, ['HINT', 'SKIP'] as const, `${path}.teach.action`);
    if (action === 'HINT') {
      const hint = exactObject(object.teach, `${path}.teach`, ['action', 'propertyId']);
      identifierAt(hint.propertyId, `${path}.teach.propertyId`);
    } else {
      exactObject(object.teach, `${path}.teach`, ['action']);
    }
  }
  return value as SaveReviewEditPayload;
}
